/**
 * Ceremony-failure classifier.
 *
 * The browser's WebAuthn API surfaces failures through a tiny
 * grammar — DOMException name plus a free-form message string —
 * that doesn't, on its own, distinguish the cases an onboarding
 * UI needs to handle differently. The canonical example is
 * `NotAllowedError`, which is raised for at least four wholly
 * different situations:
 *
 *   1. The user dismissed the OS prompt themselves.
 *   2. No platform authenticator (Hello / Touch ID) was
 *      configured, so the OS rejected the call before showing
 *      anything.
 *   3. The user picked "use my phone" but no companion device
 *      was reachable (Bluetooth off, no signed-in passkey
 *      provider).
 *   4. The browser refused for a policy reason (`disable-webauthn`
 *      enterprise pref, sandboxed iframe, etc.).
 *
 * The classifier disambiguates these by combining three signals:
 *
 *   - DOMException name + message (exact and stable for some
 *     codes; only suggestive for NotAllowedError).
 *   - Wall-clock duration of the failed `.create()` / `.get()`
 *     call. A user-driven dismiss takes hundreds of ms at
 *     minimum (the OS prompt has to render and they have to
 *     tap or type); a policy / capability fast-fail returns in
 *     well under 100ms. We use 800ms as the line — well above
 *     the fast-fail upper bound, well below any plausible human
 *     interaction time.
 *   - The `CapabilitySnapshot` collected at preflight. If the
 *     platform-authenticator probe already returned `false`,
 *     a fast-failing NotAllowedError on the "this device" card
 *     is almost certainly the OS reporting "no authenticator"
 *     rather than the user dismissing.
 *
 * Output is a `CeremonyClassification` carrying a stable code,
 * a default user-facing message, a structured remedy the UI can
 * render as a button or link, and (when available) the original
 * DOMException name + message for telemetry.
 *
 * # Determinism
 *
 * Pure function: same input → same output, no globals. Tests
 * cover every branch.
 *
 * # Privacy
 *
 * The DOMException message is captured as-is here; downstream
 * consumers (telemetry reporter) are responsible for redaction
 * before transmission. Capturing it verbatim at this layer keeps
 * the classifier independently testable and gives the support
 * dialog the option to surface the raw message in a "show
 * details" affordance for the user to copy.
 */

import type { CapabilitySnapshot } from "./device-capabilities";
import type { PasskeyTransport } from "./storage/preferred-transport";

// ── Public types ───────────────────────────────────────────────────────

/**
 * Stable error codes for ceremony failures. The set is closed —
 * adding a new code requires a coordinated change in:
 *
 *   1. The classifier (this file).
 *   2. The user-facing copy table (Slice C).
 *   3. The backend client-telemetry schema's regex on `code`
 *      (already permissive).
 *
 * Codes follow `lower_snake` convention to match the wire
 * format on `/api/client-telemetry`.
 */
export type ClassifiedCeremonyErrorCode =
  | "user_cancelled"
  | "no_authenticator_available"
  | "platform_unavailable"
  /**
   * The user has at least one enrolled credential, but none of
   * them can be satisfied by the current device. Distinct from
   * `platform_unavailable` (which fires for fresh-account devices
   * with no credentials yet — there, "set up Hello here" is the
   * correct remedy). For known users in this state, "set up Hello"
   * is misleading: enabling Windows Hello on Device 2 doesn't
   * summon a credential bound to Device 1's TPM. The honest
   * remedy is "sign in from where you enrolled" or "recover."
   *
   * Fired when:
   *   - Fast-fail NotAllowedError (no usable credential found)
   *   - hasEnrolledCredentials = true (user has registered passkeys)
   *   - The local device probe says no platform authenticator
   *     OR no hybrid feasibility for the matching transport
   *
   * The shipping copy points to the recovery flow as the primary
   * action, with "sign in from another device" as the alternative.
   * No setup deep link — that branch belongs to platform_unavailable.
   */
  | "credentials_on_other_device"
  | "hybrid_unreachable"
  | "already_registered"
  /**
   * A tenant passkey cap refused the registration (docs/security.md).
   * The server said so explicitly — 409 `passkey-limit-reached`
   * (or `hardware-key-limit`) from the ceremony's begin or finish
   * leg — so this is a POLICY decision, not a failure: retrying
   * the same ceremony cannot succeed, and nothing was written.
   *
   * It exists because a server refusal used to be indistinguishable
   * from an outage. Every non-DOMException error in the begin phase
   * became `begin_failed`, whose copy says "we couldn't reach the
   * sign-in service ... check your internet connection", with "Try
   * again" as the CTA. Observed 2026-09-08: a user at their
   * tenant's one-security-key cap was told the service was down and
   * pressed Try again fifteen times against a server that was
   * answering correctly and instantly every time.
   */
  | "passkey_limit_reached"
  /**
   * The tenant does not offer the enrollment card the user picked
   * (403 `hint-not-offered`, `tenants.enrollment_transports`). A
   * policy decision like the cap above: the server named it, and
   * retrying the same card reproduces it. A well-behaved picker
   * hides the card, so this is reached from a surface that has not
   * adopted the allowlist (the admin recovery page) or a stale
   * client — and it must still read as "pick another type", never
   * as an outage.
   */
  | "hint_not_offered"
  | "wrong_authenticator"
  | "timeout"
  | "not_supported"
  | "security_error"
  | "abort"
  | "begin_failed"
  | "verify_failed"
  | "ceremony_failed";

/** Which step of the ceremony the failure was observed in. */
export type CeremonyPhase =
  | "preflight"
  | "begin"
  | "create"
  | "get"
  | "finish";

/**
 * The transport the user (or the auto-flow) attempted to use —
 * the SAME vocabulary as the UI's `PasskeyTransport` (this is
 * deliberately the one, shared internal transport vocabulary; see
 * the P2-2 note below). `undefined` means the caller hasn't picked
 * a transport yet (preflight) or there was no caller bias (a
 * browser-driven discoverable assertion).
 *
 * P2-2 (2026-05-21): this type used to read
 * `"this_device" | "phone" | "security_key" | "conditional"` —
 * a SECOND, synonymous vocabulary (`phone` ≡ the UI's
 * `other_device`) plus a non-transport member (`conditional`, the
 * discoverable/autofill mediation, which isn't a transport at all).
 * Both were collapsed: the transport axis is now exactly the three
 * real transports, identical to `PasskeyTransport`; "no transport
 * picked" is `undefined`; and conditional mediation rides the
 * separate `conditional_mediation` telemetry flag, not this slot.
 */
export type AttemptedTransport = PasskeyTransport;

/**
 * Identity over the real transports — kept as the single, named
 * chokepoint every ceremony surface (enrollment wizard, recovery,
 * portal add-a-passkey, step-up) routes its transport telemetry
 * through. After the P2-2 collapse the classifier/telemetry
 * vocabulary IS `PasskeyTransport`, so there is no longer a
 * translation to do — but the chokepoint stays so the two can
 * never silently re-diverge (a future `PasskeyTransport` member
 * fails the build here until it's deliberately handled).
 */
export function attemptedTransportFor(
  t: PasskeyTransport,
): AttemptedTransport {
  switch (t) {
    case "this_device":
      return "this_device";
    case "other_device":
      return "other_device";
    case "security_key":
      return "security_key";
  }
}

/**
 * A structured next-step the UI surfaces to the user. Each
 * `kind` maps to one CTA design in the `CeremonyError` UI
 * primitive. Adding a new kind requires a matching UI branch.
 */
export type CeremonyRemedy =
  | { kind: "retry" }
  | { kind: "try_other_transport"; preferred?: AttemptedTransport }
  | { kind: "enable_platform_auth" }
  | { kind: "enable_bluetooth" }
  | { kind: "register_first" }
  | { kind: "contact_support" };

/** The classifier's verdict for a single ceremony failure. */
export interface CeremonyClassification {
  code: ClassifiedCeremonyErrorCode;
  /** Default operator-friendly message. UI layer overrides for end-user copy. */
  message: string;
  remedy: CeremonyRemedy;
  /** Original DOMException details for telemetry. Null for non-DOM failures. */
  domException: { name: string; message: string } | null;
}

/** Inputs to the classifier. All fields are required. */
export interface ClassifyContext {
  /** The thrown error from `navigator.credentials.{get,create}()` or a fetch call. */
  err: unknown;
  /** Wall-clock ms between ceremony attempt start and the terminal event. */
  durationMs: number;
  /** Which phase produced this error. */
  phase: CeremonyPhase;
  /** The transport the user (or auto-flow) was on. Optional for preflight. */
  transport?: AttemptedTransport;
  /** The capability snapshot collected at preflight. */
  capability: CapabilitySnapshot;
  /**
   * Whether the user has any enrolled credentials at all.
   * Threaded from /identify's available_transports via the
   * runner; true when the array is non-empty, false otherwise.
   *
   * Drives the disambiguation between platform_unavailable
   * (fresh user, no credentials, "set up Hello" is the right
   * remedy) and credentials_on_other_device (returning user
   * whose credentials live elsewhere, "set up Hello here"
   * would be misleading). When undefined, the classifier
   * falls back to the legacy behaviour (platform_unavailable
   * dominates) — pre-Item-5 callers don't break.
   */
  hasEnrolledCredentials?: boolean;
}

// ── Constants ──────────────────────────────────────────────────────────

/**
 * Threshold below which a NotAllowedError is treated as a
 * capability / policy fast-fail rather than a user-driven
 * dismissal. Tuned to be:
 *
 *   - Well above the observed fast-fail ceiling (~50–200ms in
 *     production traces).
 *   - Well below the floor of human interaction time (a user
 *     must perceive the prompt, decide, and act — even a
 *     reflexive "Cancel" tap is rarely under 1500ms).
 *
 * Tests assert both sides of the boundary. Operators tune via
 * code review, not env var; misclassification is rare and the
 * remedy CTAs are non-destructive ("try another method") so a
 * mis-bucketed event still leads the user somewhere useful.
 */
export const FAST_FAIL_THRESHOLD_MS = 800;

// ── Server refusals ───────────────────────────────────────────────────

/**
 * The RFC 9457 problem codes that are a DELIBERATE server refusal
 * of the ceremony, mapped to the classification each one deserves.
 *
 * A refusal is not a failure: the server was reachable, answered
 * fast, and said no for a reason it named. Anything in this table
 * must therefore never be bucketed as `begin_failed` /
 * `verify_failed` — those tell the user to check their connection
 * and retry, and retrying a policy decision just reproduces it.
 *
 * Keys are the backend's `code` field (lowercase-kebab, per the
 * Problem contract in core/platform/http/errors.go). They are part
 * of the API surface: the emitters are
 * `internal/identity/ceremony/handler.go` (writeServiceError) and
 * `internal/identity/phonechannel/handler.go`, and adding a code
 * here without the backend emitting it is dead weight, not
 * defence.
 */
const SERVER_REFUSALS: Readonly<
  Record<string, { code: ClassifiedCeremonyErrorCode; remedy: CeremonyRemedy }>
> = {
  // docs/security.md tenant caps. `try_other_transport` because the caps are
  // PER KIND: a user at the security-key ceiling can still hold a
  // synced or device-bound passkey, and the picker is where they see
  // which. The copy also names the two other ways out (remove one,
  // ask an administrator) for the case where the total cap bound.
  "passkey-limit-reached": {
    code: "passkey_limit_reached",
    remedy: { kind: "try_other_transport" },
  },
  // The legacy one-hardware-key index (credential.ErrHardwareKeyLimitExceeded).
  // No database built from this tree carries the index any more; a
  // retained one that still does answers this code, and the
  // user-facing situation and remedy are the same as the cap's.
  "hardware-key-limit": {
    code: "passkey_limit_reached",
    remedy: { kind: "try_other_transport" },
  },
  // The tenant's enrollment-card allowlist refused the hint
  // (ceremony.HintPermitted). Both spellings are live: the ceremony,
  // account-setup, recovery and invite-wizard begins write the kebab
  // form, the TAP enrollment begin writes snake_case like the rest of
  // its codes.
  "hint-not-offered": {
    code: "hint_not_offered",
    remedy: { kind: "try_other_transport" },
  },
  hint_not_offered: {
    code: "hint_not_offered",
    remedy: { kind: "try_other_transport" },
  },
  // The passkey is already on the account: there is nothing to fix
  // and nothing to retry. The DOMException path already reaches this
  // classification via InvalidStateError; this is the same verdict
  // when the SERVER is the one that noticed.
  "credential-already-registered": {
    code: "already_registered",
    remedy: { kind: "register_first" },
  },
};

/**
 * Read the `code` and `detail` off a Problem-shaped API error.
 *
 * Structural, not `instanceof ApiError`: this runs on an error that
 * has crossed a package boundary (the app's api-client threw it,
 * `CeremonyError` carried it on `.cause`, the page catch handed it
 * back), and a duplicated class identity across bundles would make
 * an `instanceof` check silently fail — the exact failure mode this
 * function exists to prevent. The shape is the contract.
 */
function asProblem(err: unknown): { code: string; detail: string } | null {
  if (!err || typeof err !== "object") return null;
  const status = (err as { status?: unknown }).status;
  if (typeof status !== "number") return null;
  const body = (err as { body?: unknown }).body;
  if (!body || typeof body !== "object") return null;
  const code = (body as { code?: unknown }).code;
  if (typeof code !== "string" || code === "") return null;
  const detail = (body as { detail?: unknown }).detail;
  return { code, detail: typeof detail === "string" ? detail : "" };
}

/**
 * Classify a named server refusal, or null when the error is not
 * one. Exported so a surface that catches an API error OUTSIDE a
 * ceremony runner can reach the same verdict.
 */
export function classifyServerRefusal(
  err: unknown,
): CeremonyClassification | null {
  const problem = asProblem(err);
  if (!problem) return null;
  const refusal = SERVER_REFUSALS[problem.code];
  if (!refusal) return null;
  return {
    code: refusal.code,
    // The server's own `detail` — it is written for the user and
    // carries the specifics the static copy table cannot (which cap,
    // and the numbers ride alongside as problem extensions). The UI
    // renders the copy table; this reaches telemetry and the
    // no-classification fallback block.
    message: problem.detail || `Server refused the ceremony: ${problem.code}.`,
    remedy: refusal.remedy,
    domException: null,
  };
}

// ── Public API ─────────────────────────────────────────────────────────

/**
 * Classify a ceremony failure. Always succeeds — unrecognised
 * inputs fall through to `ceremony_failed` with a `retry` remedy.
 */
export function classifyCeremonyFailure(ctx: ClassifyContext): CeremonyClassification {
  // 0. A refusal the server NAMED wins over every heuristic below.
  // Nothing about the device, the duration, or the phase can
  // improve on the server having said which policy refused and
  // why, and the heuristics would otherwise read a policy decision
  // as an outage. Runs before the DOMException branch because a
  // rejected fetch is never a DOMException in the first place.
  const refusal = classifyServerRefusal(ctx.err);
  if (refusal) return refusal;

  const dom = asDOMException(ctx.err);
  const domDetails = dom ? { name: dom.name, message: dom.message } : null;

  // 1. Non-DOMException error: phase decides the bucket.
  if (!dom) {
    if (ctx.phase === "begin") {
      return {
        code: "begin_failed",
        message: "Could not start the passkey ceremony.",
        remedy: { kind: "retry" },
        domException: domDetails,
      };
    }
    if (ctx.phase === "finish") {
      return {
        code: "verify_failed",
        message: "The passkey could not be verified by the server.",
        remedy: { kind: "retry" },
        domException: domDetails,
      };
    }
    return {
      code: "ceremony_failed",
      message: "The passkey ceremony could not complete.",
      remedy: { kind: "retry" },
      domException: domDetails,
    };
  }

  // 2. DOMException by name. The exact-name codes have stable
  //    semantics across browsers; we map them directly.
  switch (dom.name) {
    case "TimeoutError":
      return {
        code: "timeout",
        message: "The authenticator did not respond in time.",
        remedy: { kind: "retry" },
        domException: domDetails,
      };

    case "InvalidStateError":
      // Registration: the authenticator already has a credential
      // for this user. Authentication: the credential the user
      // selected isn't bound to the account they're signing in
      // as (very rare; usually means a stale allowCredentials
      // cache).
      if (ctx.phase === "create") {
        return {
          code: "already_registered",
          message: "This authenticator is already registered for your account.",
          remedy: { kind: "register_first" },
          domException: domDetails,
        };
      }
      return {
        code: "wrong_authenticator",
        message: "The selected passkey isn't tied to this account.",
        remedy: { kind: "try_other_transport" },
        domException: domDetails,
      };

    case "NotSupportedError":
      return {
        code: "not_supported",
        message: "This browser doesn't support the requested authenticator type.",
        remedy: { kind: "contact_support" },
        domException: domDetails,
      };

    case "SecurityError":
      // Raised for RP ID mismatch (cross-domain misconfig),
      // insecure context, or sandboxed iframe. Almost always an
      // operator/config issue, not something the user can fix.
      return {
        code: "security_error",
        message: "The browser blocked the passkey ceremony for security reasons.",
        remedy: { kind: "contact_support" },
        domException: domDetails,
      };

    case "AbortError":
      // The caller passed an AbortSignal and aborted it (e.g.
      // navigation, modal close). Treat as a clean abort, not a
      // failure — the UI typically just dismisses the dialog.
      return {
        code: "abort",
        message: "The passkey ceremony was cancelled.",
        remedy: { kind: "retry" },
        domException: domDetails,
      };

    case "NotAllowedError":
      return classifyNotAllowed(ctx, domDetails);

    default:
      return {
        code: "ceremony_failed",
        message: dom.message || dom.name,
        remedy: { kind: "retry" },
        domException: domDetails,
      };
  }
}

// ── Internals ──────────────────────────────────────────────────────────

/**
 * NotAllowedError is the ambiguous case. The browser uses it for:
 *
 *   - User dismissed the OS prompt
 *   - OS reported no authenticator satisfying the policy
 *   - Hybrid (phone-QR) flow couldn't reach a companion device
 *   - Enterprise / browser policy blocked WebAuthn entirely
 *
 * The duration + capability + transport signals split these.
 */
function classifyNotAllowed(
  ctx: ClassifyContext,
  domDetails: { name: string; message: string } | null,
): CeremonyClassification {
  const fastFail = ctx.durationMs < FAST_FAIL_THRESHOLD_MS;

  // Slow path: user actually saw the prompt and dismissed it.
  // The duration heuristic here is the strongest signal we have
  // — once a UI is shown, even a reflexive cancel takes well
  // over a second. Reaching this branch means we should NOT
  // tell the user their device is broken.
  if (!fastFail) {
    return {
      code: "user_cancelled",
      message: "You cancelled the passkey prompt.",
      remedy: { kind: "retry" },
      domException: domDetails,
    };
  }

  // Fast-fail branches. Use the transport + capability snapshot
  // to narrow down which capability is actually missing.

  if (ctx.transport === "other_device") {
    // Hybrid flow couldn't establish a Bluetooth or cloud-relay
    // path to a companion device. The remedy is split between
    // "turn on Bluetooth on both ends" and "use a different
    // method"; the UI surfaces both.
    return {
      code: "hybrid_unreachable",
      message:
        "Couldn't reach your phone. Bluetooth must be on for both this computer and your phone.",
      remedy: { kind: "enable_bluetooth" },
      domException: domDetails,
    };
  }

  if (ctx.transport === "this_device" && ctx.capability.platformAuthenticator === false) {
    // We KNOW the platform authenticator is unavailable (the
    // probe explicitly returned false). The remedy depends on
    // whether the user already has enrolled credentials:
    //
    //   - hasEnrolledCredentials === true → telling them to
    //     "set up Hello here" is misleading. Their credentials
    //     are bound to a different device's TPM; enabling
    //     Hello on THIS device wouldn't make those credentials
    //     usable here. The honest remedy is recovery / sign in
    //     from the original device. Route to
    //     credentials_on_other_device.
    //
    //   - Anything else (false / undefined): the legacy
    //     platform_unavailable branch — the user is enrolling
    //     fresh and "set up Hello first" IS the right next
    //     step. Pre-Item-5 callers that don't thread the
    //     hasEnrolledCredentials signal also land here, which
    //     preserves the original UX without breakage.
    if (ctx.hasEnrolledCredentials === true) {
      return {
        code: "credentials_on_other_device",
        message:
          "Your passkey isn't on this device. Sign in from where you enrolled it, or recover your account.",
        remedy: { kind: "contact_support" },
        domException: domDetails,
      };
    }
    return {
      code: "platform_unavailable",
      message:
        "This device doesn't have a passkey set up. Add a fingerprint, face unlock, or PIN in your operating system, then try again.",
      remedy: { kind: "enable_platform_auth" },
      domException: domDetails,
    };
  }

  if (ctx.capability.platformAuthenticator === false) {
    // Platform unavailable but we don't know which transport was
    // attempted (or it was security_key/conditional). Same
    // hasEnrolledCredentials disambiguation as above — for known
    // users, "your credentials are elsewhere" is honest; for
    // fresh enrollees the generic try-other-transport copy
    // applies.
    if (ctx.hasEnrolledCredentials === true) {
      return {
        code: "credentials_on_other_device",
        message:
          "Your passkey isn't on this device. Sign in from where you enrolled it, or recover your account.",
        remedy: { kind: "contact_support" },
        domException: domDetails,
      };
    }
    return {
      code: "no_authenticator_available",
      message:
        "No passkey on this device matched. Try using your phone or a security key.",
      remedy: { kind: "try_other_transport", preferred: "other_device" },
      domException: domDetails,
    };
  }

  // We don't have enough signal to narrow further: the
  // fast-fail happened on a device that MIGHT have a platform
  // authenticator (probe returned null or true) and we can't
  // attribute the failure to one transport. Give the user the
  // generic-but-actionable remedy.
  return {
    code: "no_authenticator_available",
    message:
      "No passkey could complete the sign-in. Try a different method or a security key.",
    remedy: { kind: "try_other_transport" },
    domException: domDetails,
  };
}

/**
 * Narrow `unknown` to DOMException.
 *
 * The check is order-sensitive:
 *
 *   1. `instanceof DOMException` is the trustworthy answer when
 *      DOMException is defined (every browser, jsdom, deno).
 *   2. If DOMException is undefined (pure Node, no DOM shim) we
 *      fall back to a structural check that REQUIRES the input
 *      to NOT be an `instanceof Error` — otherwise an ordinary
 *      `new Error()` would be miscategorised as a DOMException
 *      because Error also exposes `name` + `message`. The
 *      duck-typed escape hatch is only there for portable tests
 *      that explicitly construct a plain object with the
 *      DOMException shape; production code never goes through it.
 */
function asDOMException(err: unknown): DOMException | null {
  if (err == null) return null;
  if (typeof DOMException !== "undefined" && err instanceof DOMException) {
    return err;
  }
  if (err instanceof Error) {
    // Regular Error subclasses are NOT DOMExceptions — bail
    // before the duck-type fallback even looks.
    return null;
  }
  if (
    typeof err === "object" &&
    err !== null &&
    "name" in err &&
    typeof (err as { name: unknown }).name === "string" &&
    "message" in err &&
    typeof (err as { message: unknown }).message === "string"
  ) {
    return err as unknown as DOMException;
  }
  return null;
}
